/**
 * @file Grid.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Wednesday, 10th July 2019 5:26:07 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */

import type { ID, WaterlineQuery, WaterlineWhere, ColumnDefinition, iDataProvider, iDataManager } from 'web-panel-essentials/types'
import type { iEventEmitter, iLogger, iNotificationManager, iGlobalEventBus, EmitterEvents } from './../../types'

import './GridManager'
import E from 'web-panel-essentials/errors'
import { __, NOTIFICATION, getTextWidth, delay, genTestName, isTestingMode, isMobileMode, callLater } from 'web-panel/globals'
import { KEY, FORMAT, CAST } from 'web-panel-essentials/misc'

import cn from 'classnames'
import * as React from 'react'
import { ContextMenuTrigger } from 'react-contextmenu'

import Draggable from 'react-draggable'

import { Grid as ReactVirtualized, AutoSizer, ScrollSync } from 'react-virtualized'
import { prepareCSVData, exportToCSV } from './CSVExporting'
import { rowAllowed } from './filtration'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faSortAmountUp, faSortAmountDown, faDownload, faCog, faBackspace } from '@fortawesome/free-solid-svg-icons'
import autoBind from 'react-autobind'

import { Inject, Injectable } from '../../serviceLocator'

import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import range from 'lodash/range'
import isFunction from 'lodash/isFunction'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import EventBus from '../../utils/EventBus'
import DefaultCell from './defaultCell'

import { Subject, interval } from 'rxjs'
import worker from './grid.async-worker'
import simulatedWorker from './shared'
import HeaderFilterCell from './headerFilterCell'
import omit from 'lodash/omit'

import { tap, throttle as rxthrottle } from 'rxjs/operators'

library.add(faSortAmountUp, faSortAmountDown, faDownload, faCog, faBackspace)

const SORTING_ICONS = {
  ASC: 'sort-amount-up',
  DESC: 'sort-amount-down'
}

export const SORT_MODE = {
  ASC: 'ASC',
  DESC: 'DESC'
}

const OPERATION = {
  INSERT: '+',
  UPDATE: '*',
  REPLACE: '/',
  REMOVE: '-'
}

const EVENT = {
  CLICK: 'event-click',
  DOUBLE_CLICK: 'event-double-click',
  SELECTION_CHANGED: 'event-selection-changed',
  OPEN_SETTINGS: 'event-open-settings'
}

const operation = Symbol('@operation')

class HeaderControl extends React.PureComponent<any> {
  handleClick = () => {
    this.props.onClick(this.props.column)
  }

  render () : React.Node {
    const { sortingMode, column } = this.props
    const icon = SORTING_ICONS[sortingMode]

    return (
      <div className='controls' onClick={this.handleClick}>
        <span className='header-title'>{column.alias || column.title || column.name}</span>
        {sortingMode ? <FontAwesomeIcon className='icon' icon={icon} /> : null}
      </div>
    )
  }
}

/**
 * Grid may have multiple instances, and globalEventBus is unable
 * to register the same events several times
 * Can be used by separated webHoosk like in example below:
 * @example
 *   ...
 *   @Inject globalEventBus : iEventEmitter
 *   ...
 *     this.globalEventBus.on('grid-double-click', ({name, record}) => {
 *        if (name === 'orders') this.showCard({...record, isNew: false})
 *     })
 *   ...
*/
@Injectable('gridEventMediator')
class GridEventMediator extends EventBus {
  @Inject globalEventBus : iEventEmitter & iGlobalEventBus

  constructor () {
    super()

    const bus = this.globalEventBus
    if (!bus.eventRegistered('grid-click')) bus.registerEvent('grid-click')
    if (!bus.eventRegistered('grid-double-click')) bus.registerEvent('grid-double-click')
    if (!bus.eventRegistered('grid-selection-changed')) bus.registerEvent('grid-selection-changed')

    this.on(EVENT.CLICK, (...args) => this.globalEventBus.emit('grid-click', ...args))
    this.on(EVENT.DOUBLE_CLICK, (...args) => this.globalEventBus.emit('grid-double-click', ...args))
    this.on(EVENT.SELECTION_CHANGED, (...args) => this.globalEventBus.emit('grid-selection-changed', ...args))
  }

  dispose () {
    this.removeAllListeners()
  }
}

type Record = {
  id: any
}

type GridOptions<T: Record> = {
  name: string,
  dataProvider: iDataProvider<T>,
  dataManager?: ?iDataManager<T>,
  conceal?: boolean,
  selectionMode?: boolean,
  filter?: ?WaterlineQuery,
  sorting?: Object,
  autofetch?: boolean,
  autorefresh?: number,
  visible?: Array<string>,
  contextMenuId?: string,
  onClick?: Function,
  onDoubleClick?: Function,
  onContextMenuClick?: Function,
  onSelectionChanged?: Function,
  onBeforeItemInserted?: Function,
  onFilteredItemsChange?: Function,
  className?: string,
  cellClass?: string,
  noHeader?: boolean,
  noSettings?: boolean,
  scale?: number,
  testName?: string,
  useWorker?: boolean,
  patchDelay?: number
}

export type State<T> = {
  columns: Array<ColumnDefinition<T>>,
  visibleColumns: Array<ColumnDefinition<T>>,
  selectedRecords: Array<ID>,
  filter: ?WaterlineQuery,
  localFilter: WaterlineQuery,
  filtered: Array<Object>,
  placeholder: {
    text: string,
    blink?: boolean
  } | null,
  cursor: number | null,
  sorting: Object,
  changed: boolean,
  _changeTrigger: boolean,
  scrollToColumn?: ?number,
  scrollToRow?: ?number,
  headerFilters: Object,
  manualAdjustments: {[string]: number}
}

class Grid<T:Record> extends React.Component<GridOptions<T>, State<T>> {
  @Inject gridEventMediator : iEventEmitter & { dispose: Function }
  @Inject eventBus : iEventEmitter
  @Inject logger : iLogger
  @Inject notificationManager : iNotificationManager
  @Inject gridManager: any // debug
  list: Map<ID, T>

  worker: any
  name: string
  componentUmounted: boolean
  inserts: typeof Subject
  updates: typeof Subject
  replaces: typeof Subject
  deletions: typeof Subject
  changes: any

  grid: React.ElementRef<typeof ReactVirtualized>
  gridHeader: React.ElementRef<typeof ReactVirtualized>
  renderInfo: any
  testName: string
  refreshInterval: any
  _navigateThrottled: (any) => any
  _scrollToRowThrottled: (any) => any
  _onLocalFilterChangedDebounced: (any) => any
  _dataRefetchDebounced: (any) => any
  touchedCells: Set<ID>

  static get EVENT () : EmitterEvents {
    return EVENT
  }

  get EVENT () : EmitterEvents {
    return Grid.EVENT
  }

  get scale () : number {
    if (isMobileMode()) return 1.20
    return this.props.scale || 1
  }

  constructor (props: GridOptions<T>) {
    super(props)
    autoBind(this)
    this.props = props
    this.logger.namespace = 'grid'
    this.name = this.props.name || 'Generic grid'
    this.testName = this.props.testName || this.name
    if (!props.dataProvider) throw E('E_NNO_-8', 'NO_DATA_PROVIDER_PROVIDED')

    this.touchedCells = new Set()
    this.gridManager.proxy(this.props.name, props.dataProvider, props.filter)

    this.list = new Map()

    this.state = {
      cursor: null,
      placeholder: { text: __('NO_ITEMS_TO_DISPLAY'), blink: false },
      scrollToRow: null,
      selectedRecords: [],
      columns: [],
      visibleColumns: [],
      filter: props.filter,
      localFilter: {},
      sorting: props.sorting || {},
      filtered: [],
      changed: false,
      headerFilters: {},
      _changeTrigger: false,
      manualAdjustments: {}
    }

    this.bindContext()

    const refreshInterval = CAST.Number(props.autorefresh)

    if (refreshInterval > 0) {
      this.refreshInterval = async () => {
        this.refresh()
        await delay(refreshInterval)
        if (this.componentUmounted) return
        callLater(this.refreshInterval, 5000)
      }
      this.refreshInterval()
    }

    this.grid = React.createRef()
    this.gridHeader = React.createRef()
  }

  bindContext () {
    this._navigateThrottled = throttle(this._navigate.bind(this), 125)
    this._scrollToRowThrottled = throttle(this._scrollToRow.bind(this), 300)
    this._onLocalFilterChangedDebounced = debounce(this._onLocalFilterChanged.bind(this), 300)

    this._dataRefetchDebounced = debounce((filter) => {
      this.logger.info('Refreshing...')
      this.setState({ filter })
      this.fetch()
    }, 100)
  }

  /**
   * We will be inactive when hidden
   * We will re-fetch the data if a new filter has arrived.
   * @param {*} props
   */
  UNSAFE_componentWillReceiveProps (props: GridOptions<T>) { // eslint-disable-line
    if (!isEqual(props.filter, this.state.filter)) this._dataRefetchDebounced(props.filter)
    if (!isEqual(props.dataProvider, this.props.dataProvider)) this._dataRefetchDebounced(props.filter)
    if (!isEqual(props.dataManager, this.props.dataManager)) this._dataRefetchDebounced(props.filter)
  }

  async componentDidMount () {
    this.worker = this.props.useWorker ? worker() : simulatedWorker()
    await this.worker.setLocale(window.I18N.defaultLocale || 'en')
    this.changes = new Subject()

    const patchDelay = () => interval(isNil(this.props.patchDelay) ? 1000 : this.props.patchDelay)
    const bufferPipe = this.props.patchDelay === 0 ? tap() : rxthrottle(patchDelay, { leading: true, trailing: true })

    this.changes.pipe(
      tap((change) => {
        const list = this.list
        const op = change[operation]
        change = omit(change, operation)
        switch (op) {
          case OPERATION.INSERT:
          case OPERATION.REPLACE:
            list.set(change.id, change)
            break
          case OPERATION.UPDATE:
            list.set(change.id, { ...list.get(change.id), ...change })
            break
          case OPERATION.REMOVE:
            list.delete(change.id)
            break
        }
      }),
      bufferPipe
    ).subscribe(async () => await this.applyNewList(this.list))

    const { dataManager, autofetch, onBeforeItemInserted } = this.props

    if (dataManager) {
      const pushChange = (payload, type) => {
        this.preValidate(payload)
        this.changes.next(this.mark(payload, type))
      }
      const exist = (record: Object) => this.list.has(record.id)

      dataManager.activate({
        insertRecord: (record) => {
          if (exist(record)) throw E('E_RREC-9', __('RECORD_IS_ALREADY_EXIST'))
          if (!this.recordAllowed(record)) return
          if (onBeforeItemInserted) onBeforeItemInserted(record)
          pushChange(record, OPERATION.INSERT)
        },
        updateRecord: (record) => {
          // TODO: Here can be a $Shape of record
          if (!exist(record)) throw E('E_RREC-B', __('RECORD_NOT_FOUND'))
          pushChange(record, OPERATION.UPDATE)
        },
        upsertRecord: (record) => {
          // TODO: Here must be the whole record
          if (exist(record)) pushChange(record, OPERATION.UPDATE)
          else pushChange(record, OPERATION.INSERT)
        },
        replaceRecord: (record) => {
          if (exist(record)) pushChange(record, OPERATION.REPLACE)
          else throw E('E_RREC-C', __('RECORD_NOT_FOUND'))
        },
        recordExist: (id: ID) => exist({ id }),
        removeRecordById: (r) => pushChange({ id: r }, OPERATION.REMOVE),
        recordDisplayed: this.isRecordDisplayed.bind(this),

        getRecords: () => [...this.list.values()],
        getFilteredRecords: () => this.state.filtered,

        getSelection: this.getSelection.bind(this),
        getSelectedRecords: this.getSelectedRecords.bind(this),
        setSelection: this.setSelection.bind(this),
        scrollToBottom: this.scrollToBottom.bind(this),
        fetch: this.fetch.bind(this),
        cropTo: this.cropTo.bind(this)
      })
    }

    if (autofetch) this._dataRefetchDebounced(this.state.filter)

    // When gridManager's OK button pressed, rerendering grid columns
    this.gridManager.on(this.gridManager.EVENT.DATA_CHANGED, async () => {
      await this.setStateAsync({ manualAdjustments: {} })
      await this.fetch()
      this.gridHeader.current.recomputeGridSize()
      this.grid.current.recomputeGridSize()
    })
  }

  componentWillUnmount () {
    this.worker.terminate()
    if (this.props.dataManager) this.props.dataManager.deactivate()
    this.gridManager.removeAllListeners()

    this.componentUmounted = true

    this.changes.unsubscribe()
    this.gridEventMediator.dispose()
  }

  static throwIfFilterInvalid (filter: WaterlineQuery) {
    const allowedKeys = ['where', 'limit', 'skip', 'select']
    Object.keys(filter).forEach((key) => {
      if (!allowedKeys.includes(key)) throw E('E_IINV-F', 'INVALID_FILTRATION_QUERY')
    })
  }

  async fetch () : Promise<Array<Object>> {
    this.logger.info('Fetching grid records')
    await this.reset()
    await this.showPlaceholder(__('LOADING'), true)
    let payload = []
    let list = new Map()

    let { filter } = this.state
    filter = { ...filter }

    try {
      if (filter) Grid.throwIfFilterInvalid(filter)
    } catch (e) {
      this.notificationManager.show({
        message: __('INVALID_FILTER_EXPRESSION'),
        type: NOTIFICATION.ERROR,
        details: __('SEE_LOGS_FOR_DETAILS'),
        persistent: true
      })
      throw new Error(e)
    }

    try {
      const { visible } = this.props

      let { columns } = await this.gridManager.meta()
      if (visible) {
        columns = columns.map((column: ColumnDefinition<any>) => {
          // $FlowFixMe[prop-missing]
          if (column.visibilityOverwrited) return column
          else column.visible = visible.includes(column.name)

          return column
        })
      }

      if (filter && filter.select) {
        const extraSelect = columns.filter(column => column.visibilityOverwrited).map(column => column.name)
        if (filter.select) filter.select = [...filter.select, ...extraSelect]
        // $FlowFixMe[prop-missing]
        // $FlowFixMe[incompatible-use]
        columns = columns.filter((column: ColumnDefinition<any>) => (filter.select: any).includes(column.name))
      }

      const visibleColumns = columns.filter(({ visible }) => visible !== false)
      this.setState({ columns, visibleColumns })
    } catch (e) {
      this.logger.error(e)
      throw E('E_UUNA-E', 'UNABLE_TO_INITIALIZE_COLUMNS')
    }

    try {
      payload = await this.props.dataProvider.get(filter)
      list = new Map(payload.map(record => [record.id, record]))
    } catch (e) {
      // Do not handle server response if stream has been terminated manualy
      this.list = list
      if (e.body && e.body.code === 'ESTMTERM') return []
      this.showPlaceholder(__('LOAD_FAILED'), false)
      return payload
    }

    this.logger.debug('Fetched', list.size, 'records')

    await this.applyNewList(list)
    return payload
  }

  async showPlaceholder (text: string, blink: boolean = false) : Promise<void> {
    await this.setState({ placeholder: { text, blink } })
  }

  hidePlaceholder () : void {
    this.setState({ placeholder: null })
  }

  getSelection () : Array<ID> {
    return this.state.selectedRecords
  }

  setStateAsync (state: Object) : Promise<void> {
    return new Promise((resolve) => {
      this.setState(state, resolve)
    })
  }

  scrollToBottom () {
    this.grid.current && this.grid.current.scrollToCell({ rowIndex: this.state.filtered.length })
  }

  async setSelection (selectedRecords: Array<ID>, silent?: boolean) : Promise<void> {
    if (isEqual(selectedRecords, this.state.selectedRecords)) return

    await this.setStateAsync({ selectedRecords })
    if (silent) return

    const records = this.getSelectedRecords()
    if (this.props.dataManager && this.props.dataManager.selectionChanged) this.props.dataManager.selectionChanged(records)
    if (this.props.onSelectionChanged) (this.props:any).onSelectionChanged(records)
  }

  async cropTo (limit: number) {
    const limited = [...this.list.entries()].slice(-limit)
    await this.applyNewList(new Map(limited))
  }

  getFilteredRecords () : Array<any> {
    return this.state.filtered
  }

  preValidate (record: Object) : void {
    if (!record.id && record.id !== 0) throw E('E_RREC-A', 'RECORDS_WITHOUT_ID_ARE_UNSUPPORTED')
  }

  mark (record: Object, operationType: $Values<typeof OPERATION>) : Object {
    return {
      ...record,
      // $FlowFixMe
      [operation]: operationType
    }
  }

  /**
   * Посколько этот метод обновляет весь список, запускается
   * достаточно часто и блокирует основной поток, то
   * имеет смысл вынести основные вычесления в отдельный worker
   */
  async applyNewList (list: Map<ID, Object>) : Promise<Array<Object>> {
    let filtered
    this.list = list
    filtered = await this.applyFilter(this.list)
    filtered = await this.applySorting(filtered)
    this._refreshVieport(filtered)
    this.setStateAsync({ filtered })
    return filtered
  }

  /**
   * Я разделил фильтр на локальный и тот, что приходит извне.
   * Фильт извне, настолько силён, что не позволит даже добавить новую
   * запись в грид, если она не удовлетворяет его условиям.
   * Локальный же фильтр такой силы не имеет и накладывается
   * только на уже существующие данные, тем не менее, он всё еще учитывается
   * при добалении данных.
   * В этом методе оба типа фильтрации сливаются в один.
   */
  async applyFilter (list: Map<ID, any>) : Promise<Array<any>> {
    let { filter, localFilter = {} } = this.state
    filter = filter || {}

    const values = [...list.values()]
    let filtered = values

    if (!isEmpty(filter.where) || !isEmpty(localFilter.where)) {
      filtered = await this.worker.filterData(values, filter.where, localFilter.where)
    }

    let placeholderMessage
    if (filtered.length === 0) {
      placeholderMessage = __('NO_ITEMS_TO_DISPLAY')
      if (list.size) placeholderMessage = `${placeholderMessage} (${__('FILTER_APPLIED')})`
    }

    this.setState({
      placeholder: placeholderMessage ? { text: placeholderMessage, blink: false } : null,
      cursor: null
    })

    if (this.props.onFilteredItemsChange) this.props.onFilteredItemsChange(filtered)
    return filtered
  }

  async applySorting (filtered: Array<T>) : Promise<Array<T>> {
    let sorted = filtered
    for (const columnName in this.state.sorting) {
      // $FlowFixMe
      sorted = await this.sortColumn(sorted, this.state.columns.find(({ name }) => name === columnName))
    }
    this.setState({ cursor: null })
    return sorted
  }

  // https://en.wikipedia.org/wiki/Merge_sort
  async sortColumn (filtered: Array<any>, column?: ColumnDefinition<any>, toggle: boolean = false) : Promise<Array<any>> {
    if (!column) return filtered

    let mode : string = this.state.sorting[column.name]

    const getNextMode = (currentMode) : string => {
      if (!currentMode) return 'ASC'
      if (currentMode === 'ASC') return 'DESC'
      if (currentMode === 'DESC') return ''
      return ''
    }

    if (toggle) mode = getNextMode(mode)

    if (!mode) {
      const currentSorting = this.state.sorting
      delete currentSorting[column.name]
      this.setState({ sorting: currentSorting })
      return filtered
    } else {
      const columnInfo = {}
      columnInfo[column.name] = mode

      const target = column.sortMap || column.name
      const sorted = await this.worker.sortColumn(filtered, target, mode)

      this.setState({
        cursor: null,
        sorting: columnInfo //  { ...this.state.sorting, ...columnInfo } for multimode,
      })
      return sorted
    }
  }

  getFilteredIndexByRecordId (recordId: ID) : number {
    return this.state.filtered.findIndex((record, index) => record.id === recordId) // FIXME: Tooooo heavy
  }

  isRecordDisplayed (recordId: ID) : boolean {
    const index = this.getFilteredIndexByRecordId(recordId)
    if (isNaN(index)) return false
    if (!this.renderInfo) return true
    const { rowOverscanStartIndex, rowOverscanStopIndex } = this.renderInfo
    return rowOverscanStartIndex <= index && index <= rowOverscanStopIndex
  }

  reset (list?: Map<ID, Object> = new Map()) : Promise<void> {
    return new Promise(resolve => {
      this.list = list
      this.setState({ filtered: [] }, resolve)
    })
  }

  /**
   * If the user filters in the header, then
   * store it in the state as localFilter,
   * because it does not intersect with what came from outside
   * @param {*} event
   * @param {*} column
   */
  async _onLocalFilterChanged () : Promise<void> {
    const { headerFilters } = this.state
    let where : WaterlineWhere

    for (const columnName in headerFilters) {
      const filterValue = headerFilters[columnName]
      if (filterValue) where = { ...where, [columnName]: { ilike: filterValue } }
    }

    await this.setState({ localFilter: { where } })
    const filtered = await this.applyFilter(this.list)
    this.setState({ filtered })
  }

  /**
   * This method can be used to exclude "dead" records at the insertion stage,
   * Records that will never be shown to the user due to the global filter.
   * @see. DataManager
   */
  recordAllowed (record: Object) : boolean {
    const { filter } = this.state
    if (!filter || !filter.where) return true
    return rowAllowed(record, filter.where)
  }

  async setFilter (filter: WaterlineQuery) : Promise<this> {
    this.setState({ filter })
    const filtered = await this.applyFilter(this.list)
    this.setState({ filtered })
    return this
  }

  refresh () : void {
    // Do not refresh if element is concealed
    if (this.props.conceal) return
    if (!this.state.filtered || this.state.filtered.length === 0) return
    this.setState({ ...this.state, _changeTrigger: !this.state._changeTrigger })
  }

  _getRowStyle (row: Object) : {} {
    const color = row.rowFg
    const backgroundColor = row.rowBg
    const fontSize = 12 * Math.pow(this.scale, 1.5)
    return { color, backgroundColor, fontSize }
  }

  handleCellDoubleClick (opts: any) : void {
    this.eventBus.emit(EVENT.DOUBLE_CLICK, { name: this.name, ...opts })
    this.gridEventMediator.emit(EVENT.DOUBLE_CLICK, { name: this.name, ...opts })
    if (this.props.onDoubleClick) this.props.onDoubleClick(opts)
  }

  handleCellClick (opts: Object) : void {
    const { event, columnIndex, rowIndex, column, record } = opts
    this._selectCell({ event, columnIndex, rowIndex })
    this.eventBus.emit(EVENT.CLICK, { name: this.name, columnIndex, rowIndex, column, record })
    this.gridEventMediator.emit(EVENT.CLICK, { name: this.name, columnIndex, rowIndex, column, record })
    if (this.props.onClick) this.props.onClick({ event, column, record })

    if (isMobileMode()) {
      this.simulateDoubleClick(opts)
    }
  }

  // @see https://stackoverflow.com/questions/25777826/onclick-works-but-ondoubleclick-is-ignored-on-react-component
  simulateDoubleClick (opts: Object) : void {
    const id = opts.record.id
    if (this.touchedCells.has(id)) {
      this.handleCellDoubleClick(opts)
      this.touchedCells.delete(id)
      return
    }
    this.touchedCells.add(id)
    setTimeout(() => this.touchedCells.delete(id), 250)
  }

  _selectCell ({ event, columnIndex, rowIndex }: Object) : void {
    // event.persist()
    let { cursor } = this.state
    const { selectionMode } = this.props

    const record = this.state.filtered[rowIndex]
    if (!record) return

    this.setState({ cursor: rowIndex })

    if (event.shiftKey) {
      let selection = range(cursor, rowIndex).concat(rowIndex)
      selection = selection.map((index) => this.state.filtered[index].id)
      this.setSelection(selection)
    } else if (event.ctrlKey || selectionMode) {
      let { selectedRecords } = this.state
      if (selectedRecords.includes(record.id)) {
        selectedRecords = selectedRecords.filter((id) => id !== record.id)
        if (cursor === rowIndex) cursor = null
        this.setState({ cursor })
      } else selectedRecords = [...selectedRecords, record.id]
      this.setSelection(selectedRecords)
    } else {
      this.setSelection([record.id])
    }
  }

  handleClickSelect ({ event, columnIndex, rowIndex } : Object) : void {
    const { selectedRecords, filtered } = this.state
    if (!selectedRecords.length || selectedRecords.length === 1) {
      const record = filtered[rowIndex]
      this.setState({ cursor: rowIndex })
      this.setSelection([record.id])
    }
  }

  _handleExtraAction (actionName: string, payoad: Object) : void {
    if (this.props.dataManager && this.props.dataManager.extraActionEmited) {
      this.props.dataManager.extraActionEmited(actionName, payoad)
    }
  }

  _scrollToRow (rowIndex: number) : void {
    this.grid.current.scrollToCell({ rowIndex })
  }

  async _navigate (e: any) {
    const { filtered, selectedRecords } = this.state
    const cursor = CAST.Number(this.state.cursor)

    switch (e.keyCode) {
      case KEY.DOWN: {
        const nextRow = Math.min(cursor + 1, filtered.length - 1)
        if (e.shiftKey) {
          this.setSelection([...selectedRecords, filtered[cursor].id, filtered[nextRow].id])
        } else {
          this.setSelection([filtered[nextRow].id])
        }
        this.setState({ cursor: nextRow })
        this._scrollToRowThrottled(nextRow)
        break
      }
      case KEY.UP: {
        const prevRow = Math.max(cursor - 1, 0)

        if (e.shiftKey) this.setSelection([filtered[cursor].id, filtered[prevRow].id, ...selectedRecords])
        else this.setSelection([filtered[prevRow].id])

        this.setState({ cursor: prevRow })
        this._scrollToRowThrottled(prevRow)
        break
      }
    }
  }

  getSelectedRecords () : Array<T> {
    return [...this.list.values()].filter(({ id }) => this.state.selectedRecords.includes(id))
  }

  _onSectionRendered (renderInfo: Object) : void {
    this.renderInfo = renderInfo
    this._refreshVieport(this.state.filtered)
  }

  _refreshVieport (filtered: Array<any>) {
    if (this.props.dataManager && this.props.dataManager.viewportChanged) {
      if (!this.renderInfo) return // ? Не уверен
      const { rowOverscanStartIndex, rowOverscanStopIndex } = this.renderInfo
      // $FlowFixMe[extra-arg]
      this.props.dataManager.viewportChanged(filtered.slice(rowOverscanStartIndex, rowOverscanStopIndex + 1), filtered)
    }
  }

  handleOpenSettings () : void {
    this.gridManager.show()
  }

  handleInvokeExport () : void {
    const { visibleColumns = [], filtered } = this.state
    this.notificationManager.show({
      id: Symbol.for('TSV-exported'),
      message: __('DATA_EXPORTED_AS_TSV'),
      details: __('SPECIFY_TAB_AS_DELIMETER_TO_OPEN_DOCUMENT_IN_EXTERNAL_EDITOR'),
      type: NOTIFICATION.WARNING,
      persistent: true
    })
    const { titles, rows } = prepareCSVData(visibleColumns, filtered)
    exportToCSV(titles, rows)
  }

  /**
   * Auto-selection of column width, based on the width of its text
   * @param {*} columnIndex
   */
  adjustColumnWidth (columnIndex: number) {
    const ROW_MEASURE_LIMIT = 100
    const VIRTUAL_PADDING = 24
    const DEFAULT_MIN_WIDTH = 50
    const FONT_DEFINITION = 'Roboto 12px'
    const { filtered, columns } = this.state

    const width = filtered.filter((i, index) => (index < ROW_MEASURE_LIMIT)).reduce((acc, row, rowIndex) => {
      const columnName = columns[columnIndex].name
      return Math.max(acc, getTextWidth(row[columnName], FONT_DEFINITION) + VIRTUAL_PADDING)
    }, DEFAULT_MIN_WIDTH)

    columns[columnIndex].width = Math.min(width, 400)
    this.setState({ columns: [...columns] })
    this.gridHeader.current.recomputeGridSize({ columnIndex })
    this.grid.current.recomputeGridSize({ columnIndex })
  }

  resizeColumn (columnName: string, delta: number) : void {
    const MIN_WIDTH = 30
    const MAX_WIDTH = 500
    const { columns, visibleColumns, manualAdjustments } = this.state

    const columnIndex = columns.findIndex(column => column.name === columnName)
    let { width = 100, name } = columns[columnIndex]
    width += delta
    width = Math.max(MIN_WIDTH, width)
    width = Math.min(MAX_WIDTH, width)
    columns[columnIndex].width = width

    this.setState({ columns: [...columns], manualAdjustments: { ...manualAdjustments, [name]: parseInt(width, 20) } })

    const visibleColumnIndex = visibleColumns.findIndex(column => column.name === columnName)
    this.gridHeader.current.recomputeGridSize({ columnIndex: visibleColumnIndex })
    this.grid.current.recomputeGridSize({ columnIndex: visibleColumnIndex })
  }

  async handleHeaderClick (column: any) : Promise<void> {
    const filtered = await this.sortColumn(this.state.filtered, column, true)
    this.setState({ filtered })
  }

  handleHeaderFilterChange (value: string, columnName: string) : void {
    const filters = this.state.headerFilters
    this.setState({
      headerFilters: {
        ...filters,
        [columnName]: CAST.String(value)
      }
    }, this._onLocalFilterChanged)
  }

  _headerCellRenderer ({ columnIndex, key, style, rowIndex }: Object) : React.Node {
    const column = this.state.visibleColumns[columnIndex]
    const sortingMode = this.state.sorting[column.name]
    const filters = this.state.headerFilters
    const filterValue = CAST.String(filters[column.name])

    const className = cn('headerСell', { active: Boolean(filterValue) })

    if (rowIndex === 1) {
      return (
        <HeaderFilterCell
          key={key}
          value={filterValue}
          columnName={column.name}
          tabIndex={this.props.conceal ? -1 : 0}
          style={style}
          onChange={this.handleHeaderFilterChange}
        />
      )
    }
    return (
      <div title={column.title || column.name} className={className} style={style} key={key}>
        <HeaderControl sortingMode={sortingMode} column={column} onClick={this.handleHeaderClick} />
        <Draggable
          axis='x'
          defaultClassName='drag-handle'
          defaultClassNameDragging='drag-handle-active'
          onStop={(event, data) => this.resizeColumn(column.name, data.x)}
          position={{ x: 0, y: 0 }}
          zIndex={999}
        >
          <div
            className='adjust' onDoubleClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              this.adjustColumnWidth(columnIndex)
              return false
            }}
          />
        </Draggable>
      </div>
    )
  }

  _cellRenderer ({ columnIndex, key, parent, rowIndex, style, isScrolling, selectedRecords, isVisible }: Object) : React.Node {
    const { filtered, localFilter, visibleColumns } = this.state

    // На больших списках мы не можем себе позволить рендерить что-то лишнее
    // это условие сделает список более производительным, но сделает
    // прокрутку менее удобной
    if (filtered.length > 30 && !isVisible) return null

    const record = filtered[rowIndex]
    if (!record) return null

    const parentName = this.testName
    const columns = visibleColumns
    const column : ColumnDefinition<any> = columns[columnIndex]

    let active = false
    if (localFilter && localFilter.where && localFilter.where[column.name]) active = true

    const className = cn('evenRow', 'cell', {
      selected: this.state.selectedRecords.includes(record.id),
      cursor: rowIndex === this.state.cursor,
      changed: record.changed,
      active
    }, this.props.cellClass)

    style = { ...style, ...this._getRowStyle(record) }

    /* let generateTitle = (record) => _(columns).filter(c => c.visible).reduce((acc, column, index) => {
      acc += [column.title || column.name, record[column.name]].join(' : ')
      acc += '\n'
      return acc
    }, '') */

    let value
    if (column.format) {
      value = isFunction(column.format) ? (column:Object).format(record[column.name]) : FORMAT.String(record[column.name])
    } else {
      value = FORMAT.String(record[column.name])
    }

    const generateTitle = (record) => record.title || value

    if ((column:any).cellType && isFunction((column:any).cellType)) {
      return (column:any).cellType({
        grid: this,
        onClick: this.handleCellClick,
        onDoubleClick: this.handleCellDoubleClick,
        onContextMenu: this.handleClickSelect,
        onExtraAction: this._handleExtraAction,
        list: this.state.filtered,
        columnIndex,
        record,
        column,
        key,
        parent,
        parentName,
        rowIndex,
        className,
        style,
        value
      })
    }

    return (
      <DefaultCell
        key={key}
        column={column}
        record={record}
        columnIndex={columnIndex}
        rowIndex={rowIndex}
        title={generateTitle(record)}
        className={className}
        style={style}
        parentName={parentName}
        onContextMenu={this.handleClickSelect}
        onDoubleClick={this.handleCellDoubleClick}
        onClick={this.handleCellClick}
        value={value}
      />
    )
  }

  _getColumnWidth ({ index }: Object) : number {
    const column: ColumnDefinition<any> = this.state.visibleColumns[index]
    if (column && column.visible === false) return 0
    return column.width || 100
  }

  render () : React.Node {
    const {
      visibleColumns = [],
      scrollToColumn,
      scrollToRow,
      filtered,
      placeholder = {},
      selectedRecords
    } = this.state
    const { noHeader, noSettings } = this.props

    const cellHeight = this.scale * 25
    const className = cn('grid-overlay', this.props.className, {
      placeholder: placeholder && Boolean(placeholder.text),
      blink: placeholder && placeholder.blink
    })

    return (
      <ScrollSync>
        {({
          clientHeight,
          clientWidth,
          onScroll,
          scrollHeight,
          scrollLeft,
          scrollTop,
          scrollWidth
        }) => {
          return (
            <div
              {...genTestName(this.testName)}
              data-test-rows-length={isTestingMode() ? this.state.filtered.length : null}
              data-test-cols-length={isTestingMode() ? this.state.columns.filter(value => value.visible !== false).length : null}
              className={className}
              placeholder={placeholder ? placeholder.text : ''}
            >
              <AutoSizer>{({ height, width }) => (
                <>
                  {noHeader ? null : (
                    <div className='header-box' style={{ width: width, height: 46 }}>
                      <ReactVirtualized
                        className='HeaderGrid'
                        changed={this.state.changed}
                        tabIndex={-1}
                        ref={this.gridHeader}
                        columnWidth={this._getColumnWidth}
                        columnCount={visibleColumns.length}
                        height={46}
                        cellRenderer={({ columnIndex, rowIndex, key, style }) => this._headerCellRenderer({ columnIndex, rowIndex, key, style })}
                        rowHeight={23}
                        rowCount={2}
                        scrollLeft={scrollLeft}
                        width={width}
                      />
                    </div>
                  )}
                  <div
                    className='grid-box'
                    data-rh='tooltip 1'
                    onKeyDown={(e) => {
                      e.persist()
                      // e.preventDefault()
                      // e.stopPropagation()
                      this._navigateThrottled(e)
                    }}
                  >
                    <ContextMenuTrigger
                      collect={(prop) => prop}
                      id={this.props.contextMenuId || ''}
                      disableIfShiftIsPressed
                      holdToDisplay={isMobileMode() ? 1000 : -1}
                      grid={this}
                      onItemClick={this.props.onContextMenuClick}
                    >
                      <ReactVirtualized
                        containerProps={genTestName('scrolling', this.testName)}
                        tabIndex={-1}
                        overscanColumnCount={4}
                        ref={this.grid}
                        scrollingResetTimeInterval={50}
                        changed={this.state.changed}
                        className='BodyGrid'
                        cellRenderer={
                          ({ columnIndex, key, parent, rowIndex, style, isScrolling, isVisible }) => this._cellRenderer({ columnIndex, isVisible, key, parent, rowIndex, style, isScrolling, scrollToColumn, scrollToRow, selectedRecords })
                        }
                        onSectionRendered={(...args) => {
                          this._onSectionRendered(...args)
                        }}
                        columnCount={visibleColumns.length}
                        columnWidth={this._getColumnWidth}
                        onScroll={onScroll}
                        height={height - (noHeader ? 0 : 46)}
                        rowCount={filtered.length}
                        rowHeight={cellHeight}
                        estimatedColumnSize={100}
                        estimatedRowSize={cellHeight}
                        width={width}
                        noContentRenderer={() => {
                          const width = visibleColumns.reduce((acc, column, index) => acc + this._getColumnWidth({ index }), 0) + 'px'
                          return (<div style={{ width }}>&nbsp;</div>)
                        }}
                      />
                    </ContextMenuTrigger>
                  </div>
                  <div className='grid-tools'>
                    {noSettings ? null : (
                      <div className='tool' onClick={this.handleOpenSettings}>
                        <FontAwesomeIcon className='icon' icon='cog' />
                      </div>
                    )}
                    <div className='tool' onClick={this.handleInvokeExport}>
                      <FontAwesomeIcon className='icon' icon='download' />
                    </div>
                    <div className='total'>{`${__('TOTAL')}: ${filtered.length}`}</div>
                  </div>
                </>)}
              </AutoSizer>
            </div>)
        }}
      </ScrollSync>
    )
  }
}

export { GridEventMediator }
export default Grid
